import { useState, useRef, useCallback } from 'react';

/**
 * useClaude — streaming conversation with Claude via CF Worker.
 *
 * Sends finalized user transcripts to the worker, which calls the Anthropic
 * streaming API and pipes response tokens back. Supports cancellation for
 * when the user interrupts mid-response.
 *
 * Returns:
 *   response      — latest complete response text
 *   streamedText  — current partial response (updates per token)
 *   isGenerating  — whether Claude is currently generating
 *   send(text)    — send user message, start streaming response
 *   cancel()      — abort current generation (for interruptions)
 *   history       — conversation history array [{role, content}]
 *   clearHistory  — reset conversation
 */
// Module-level lock — shared across ALL instances/re-renders
let GLOBAL_LOCK = false;

export default function useClaude(settings = {}) {
  const [response, setResponse] = useState('');
  const [streamedText, setStreamedText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [history, setHistory] = useState([]);

  const abortRef = useRef(null);
  const onDeltaRef = useRef(null);
  const onCompleteRef = useRef(null);
  const historyRef = useRef([]);
  const lockRef = useRef(false);
  const settingsRef = useRef(settings);

  // Keep refs in sync
  historyRef.current = history;
  settingsRef.current = settings;

  const send = useCallback(
    async (userText, image) => {
      if (!userText.trim()) return;
      if (GLOBAL_LOCK) { console.log('[useClaude] BLOCKED by global lock'); return; }
      GLOBAL_LOCK = true;

      // Build message content — array format if image attached, plain string otherwise
      let messageContent;
      if (image) {
        messageContent = [
          { type: 'image', source: { type: 'base64', media_type: image.mediaType, data: image.base64 } },
          { type: 'text', text: userText },
        ];
      } else {
        messageContent = userText;
      }

      // Add user message to history
      const updatedHistory = [...historyRef.current, { role: 'user', content: messageContent }];
      setHistory(updatedHistory);
      setStreamedText('');
      setIsGenerating(true);

      const abortController = new AbortController();
      abortRef.current = abortController;

      try {
        const workerHttpUrl = (settingsRef.current.workerUrl || '')
          .replace('ws://', 'http://')
          .replace('wss://', 'https://')
          .replace(/\/ws\/?$/, '') + '/api/chat/stream';

        const res = await fetch(workerHttpUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: updatedHistory,
            system: settingsRef.current.systemPrompt,
          }),
          signal: abortController.signal,
        });

        if (!res.ok) {
          throw new Error(`Worker returned ${res.status}`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let fullText = '';
        let sseBuffer = '';
        let finalized = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw || raw === '[DONE]') continue;
            try {
              const data = JSON.parse(raw);
              if (data.type === 'response_delta' && data.text) {
                fullText += data.text;
                setStreamedText(fullText);
                onDeltaRef.current?.(data.text);
              } else if (data.type === 'response_end' && !finalized) {
                finalized = true;
                setResponse(fullText);
                setHistory((prev) => [...prev, { role: 'assistant', content: fullText }]);
                onCompleteRef.current?.(fullText);
              } else if (data.type === 'error') {
                console.error('[useClaude] Server error:', data.message);
              }
            } catch (_) { /* skip non-JSON lines */ }
          }
        }

        // If stream ended without response_end, finalize
        if (fullText && !finalized) {
          setResponse(fullText);
          setHistory((prev) => [...prev, { role: 'assistant', content: fullText }]);
          onCompleteRef.current?.(fullText);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('[useClaude] Stream error:', err);
        }
      } finally {
        setIsGenerating(false);
        abortRef.current = null;
        GLOBAL_LOCK = false;
      }
    },
    [settings]
  );

  const cancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsGenerating(false);
    GLOBAL_LOCK = false;

    // Save partial text (or a placeholder) to history so the next user message
    // doesn't create two consecutive user messages (which causes Anthropic 400).
    setStreamedText((current) => {
      setHistory((prev) => {
        // Only add if the last message is a user message (needs an assistant reply)
        const last = prev[prev.length - 1];
        if (last && last.role === 'user') {
          return [...prev, { role: 'assistant', content: current ? current + ' [interrupted]' : '[interrupted]' }];
        }
        return prev;
      });
      return '';
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setResponse('');
    setStreamedText('');
  }, []);

  return {
    response,
    streamedText,
    isGenerating,
    send,
    cancel,
    history,
    clearHistory,
    onDeltaRef,
    onCompleteRef,
  };
}
